import { useMemo } from 'react'
import { useAuth } from '../../../context/AuthContext'

// Thin hook over AuthContext.api / apiRaw. All endpoints live under
// /api/teams-data/* in vader, which proxies via service binding to the
// tbtv-teams-data worker. Future apps (watch, mobile) and tbtv-highlights
// entity resolution will use the same endpoint surface.
//
// Memoized for the same reason as useHighlightsApi — caller passes the
// returned object as a useEffect dep without causing infinite re-renders.
export function useTeamsDataApi() {
  const { api, apiRaw } = useAuth()

  return useMemo(() => ({
    // -------- Orgs --------
    listOrgs() {
      return api('/teams-data/orgs')
    },
    createOrg({ org_id, sport, display_name, short_name, level, notes } = {}) {
      return api('/teams-data/orgs', {
        method: 'POST',
        body: JSON.stringify({ org_id, sport, display_name, short_name, level, notes }),
      })
    },
    updateOrg(org_id, patch) {
      return api(`/teams-data/orgs/${encodeURIComponent(org_id)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      })
    },

    // -------- Org sources --------
    listOrgSources(org_id) {
      return api(`/teams-data/orgs/${encodeURIComponent(org_id)}/sources`)
    },
    createOrgSource(org_id, { url, kind, description, is_active = 1 } = {}) {
      return api(`/teams-data/orgs/${encodeURIComponent(org_id)}/sources`, {
        method: 'POST',
        body: JSON.stringify({ url, kind, description, is_active }),
      })
    },
    updateOrgSource(source_id, patch) {
      return api(`/teams-data/org-sources/${source_id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      })
    },
    deleteOrgSource(source_id) {
      return api(`/teams-data/org-sources/${source_id}`, { method: 'DELETE' })
    },

    // -------- Teams --------
    listTeams({ org_id, sport, season, division, search, limit } = {}) {
      const p = new URLSearchParams()
      if (org_id)   p.set('org_id', org_id)
      if (sport)    p.set('sport', sport)
      if (season)   p.set('season', season)
      if (division) p.set('division', division)
      if (search)   p.set('search', search)
      if (limit)    p.set('limit', String(limit))
      const qs = p.toString()
      return api(`/teams-data/teams${qs ? '?' + qs : ''}`)
    },

    // -------- Players --------
    searchPlayers({ search, org_id, sport, season, team_id, limit } = {}) {
      const p = new URLSearchParams()
      if (search)  p.set('search', search)
      if (org_id)  p.set('org_id', org_id)
      if (sport)   p.set('sport', sport)
      if (season)  p.set('season', season)
      if (team_id) p.set('team_id', team_id)
      if (limit)   p.set('limit', String(limit))
      const qs = p.toString()
      return api(`/teams-data/players${qs ? '?' + qs : ''}`)
    },
    getPlayer(player_id) {
      return api(`/teams-data/players/${encodeURIComponent(player_id)}`)
    },
    listPlayerAliases(player_id) {
      return api(`/teams-data/players/${encodeURIComponent(player_id)}/aliases`)
    },
    createPlayerAlias(player_id, { alias_form, alias_kind, confidence } = {}) {
      return api(`/teams-data/players/${encodeURIComponent(player_id)}/aliases`, {
        method: 'POST',
        body: JSON.stringify({ alias_form, alias_kind, confidence }),
      })
    },
    deletePlayerAlias(player_id, alias_form) {
      return api(`/teams-data/players/${encodeURIComponent(player_id)}/aliases/${encodeURIComponent(alias_form)}`, {
        method: 'DELETE',
      })
    },

    // -------- Upload --------
    // file = browser File / Blob; extras = { source_kind, season, dry_run }.
    // Uses apiRaw so Content-Type stays multipart/form-data with boundary.
    uploadCsv(file, { source_kind, season, dry_run = false } = {}) {
      const form = new FormData()
      form.append('file', file)
      form.append('source_kind', source_kind)
      if (season)  form.append('season', season)
      if (dry_run) form.append('dry_run', 'true')
      return apiRaw('/teams-data/upload', { method: 'POST', body: form })
    },
    listUploads({ limit = 50 } = {}) {
      return api(`/teams-data/uploads?limit=${limit}`)
    },
  }), [api, apiRaw])
}
